<template>
  <div class="product-add-review px-5 md:px-0 mt-4 md:mt-0">
    <NotificationBanner
      v-if="isReviewAdded === true"
      status="success"
      data-testid="add-review-success-message"
      :title="$t('review-successfully-submitted')"
      :subtitle="$t('few-minutes-to-be-published')"
    />
    <div
      v-else
      :class="productAddReviewFormClass"
    >
      <GenericFormError
        v-if="errorMessage"
        :error="errorMessage"
        data-testid="add-review-error-message"
        class="mb-4"
      />
      <form
        novalidate
        @submit.stop.prevent="onSubmit"
      >
        <div class="mb-4 lg:mb-6 flex flex-col lg:flex-row lg:items-center gap-6">
          <p class="lg:order-2 text-base">
            {{ $t('Leave a review (5 stars highest, 1 lowest)') }}*
          </p>
          <ReviewStarRating
            v-model="reviewForm.rating"
            name="rating"
            :valid="!ratingError"
            :error-message="ratingError"
          />
        </div>
        <PrimaryInput
          v-model="reviewForm.email"
          :label="$t('Your email address')"
          name="contact-email"  
          data-testid="add-review-email"
          type="email"
          required
          class="form__element"
          :valid="!reviewEmailError"
          :error-message="reviewEmailError"
        />
          <PrimaryInput
            v-model="reviewForm.authorName"
            :label="$t('Your name')"
            name="user-name"
            data-testid="add-review-name"
            class="form__element"
          />
          <PrimaryInput
            v-model="reviewForm.title"
            :label="$t('Title of your review')"
            name="title"
            data-testid="add-review-title"
            class="form__element"
          />
          <CustomTextarea
            v-model="reviewForm.text"
            :placeholder="$t('Your description')"
            :rows="4"
            name="text"
            data-testid="add-review-text"
            class="form__element"
          />
          <p class="text-xs mb-8 text-EXPONDO-grey-60">
            {{ $t('The fields marked with a * are mandatory') }}
          </p>
          <CustomButton
            type="primary"
            is-submit-type
            size="lg"
            data-testid="add-review-btn"
            :text="$t('Add new review')"
          />
        </form>
    </div>
  </div>
</template>
<script lang="ts">

import { useForm, useField } from 'vee-validate';
import type { GrahamProductReview } from '~~/types/product/attribute/GrahamProductReview';

import { scrollToSection } from '~~/helpers/scroll/scrollToSection';
import type { TriggerProductReviewTagsParameters } from '~~/types/integrations/tagManager/TagManagerIntegration';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import { productGetters } from '~/utils/ct';
import { addNativeRoutingToProductUrl } from '~~/helpers/routes/nativeRouting/transformProductsForNativeRouting';

export default defineComponent({
  name: 'ProductAddReview',

  props: {
    product: {
      type: Object as PropType<ProductVariantExtended>,
      default: () => {},
    },
    email: {
      type: String,
      default: '',
    },
    rating: {
      type: Number,
      default: null,
    },
  },
  setup(props) {

    const productAddReviewFormClass = 'product-add-review__form';
    const { languageAndCountry, domain, countryCode } = useLocalization();
    const { addReview, errorMessage, isReviewAdded } = useManageReviews();

    const reviewForm = ref<GrahamProductReview>({
      sku: productGetters.getSku(props.product),
      authorName: '',
      title: '',
      email: props.email,
      text: '',
      rating: props.rating,
      locale: languageAndCountry.value,
    });

    const { handleSubmit } = useForm();

    const { errorMessage: ratingError, setValue: setRatingValue } = useField('rating', 'rating', {
      initialValue: reviewForm.value.rating,
    });

    const { errorMessage: reviewEmailError, setValue: setReviewEmailValue } = useField('email', 'required|email', {
      initialValue: reviewForm.value.email,
    });

    watch(() => reviewForm.value.rating, (newVal) => {

      setRatingValue(newVal);

    });

    watch(() => reviewForm.value.email, (newVal) => {

      setReviewEmailValue(newVal);

    });

    const { trackProductReview } = useGtmEvents();

    const handleReviewSubmit = async () => {

      await addReview(reviewForm.value);

      if (isReviewAdded.value === true) {

        const ratingValue = reviewForm.value.rating;

        if (ratingValue === null || ratingValue === undefined) {

          return;

        }

        const productLink = addNativeRoutingToProductUrl(
          productGetters.getSlug(props.product),
          countryCode.value,
        );

        const productFullLink = `${domain.value}${productLink}`;

        const params: TriggerProductReviewTagsParameters = {
          product: props.product,
          languageAndCountry: reviewForm.value.locale,
          score: String(ratingValue),
          email: reviewForm.value.email,
          shop: domain.value,
          productLink: productFullLink,
        };

        trackProductReview(params);
      
      }
    
    };

    const onSubmit = handleSubmit(() => {

      handleReviewSubmit();

    });

    onMounted(() => {

      if (props.email && props.rating !== undefined) {

        scrollToSection(`.${productAddReviewFormClass}`, -100);
      
      }
    
    });

    return {
      reviewForm,
      onSubmit,
      ratingError,
      reviewEmailError,
      isReviewAdded,
      errorMessage,
      productAddReviewFormClass,
    };
  
  },
});

</script>
